import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const STORAGE_KEY = 'auth_app_theme';

const ThemeModeContext = createContext(null);

export const useThemeMode = () => {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within a ThemeModeProvider');
  return ctx;
};

const readStoredMode = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'white' ? 'white' : 'dark';
  } catch {
    return 'dark';
  }
};

const PALETTES = {
  dark: {
    mode: 'dark',
    primary: { main: '#58a6ff' },
    secondary: { main: '#f778ba' },
    error: { main: '#ff7b72' },
    background: { default: '#081120', paper: '#101b2d' },
    divider: '#1e2d45',
    text: { primary: '#e6edf3', secondary: '#8b949e' },
  },
  white: {
    mode: 'light',
    primary: { main: '#0969da' },
    secondary: { main: '#bf3989' },
    error: { main: '#cf222e' },
    background: { default: '#f6f8fa', paper: '#ffffff' },
    divider: '#d0d7de',
    text: { primary: '#1f2328', secondary: '#656d76' },
  },
};

const createAppTheme = (mode) => {
  const palette = PALETTES[mode];
  return createTheme({
    palette,
    typography: { h6: { fontSize: '1rem' } },
    shape: { borderRadius: 8 },
    components: {
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none', border: `1px solid ${palette.divider}` } },
      },
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'inherit' },
        styleOverrides: {
          colorInherit: {
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
            borderBottom: `1px solid ${palette.divider}`,
          },
        },
      },
      MuiDialog: {
        defaultProps: { maxWidth: 'xs', fullWidth: true },
        styleOverrides: { paper: { border: 'none' }, paperWidthXs: { maxWidth: 520 } },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: { fontWeight: 700, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 },
        },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: '12px 24px 20px' } },
      },
      MuiDrawer: {
        styleOverrides: { paper: { border: 'none', borderRight: `1px solid ${palette.divider}` } },
      },
      MuiTableCell: {
        styleOverrides: { root: { borderColor: palette.divider }, head: { fontWeight: 600 } },
      },
    },
  });
};

export const ThemeModeProvider = ({ children }) => {
  const [themeMode, setThemeModeState] = useState(readStoredMode);

  const setThemeMode = useCallback((next) => {
    const mode = next === 'white' ? 'white' : 'dark';
    setThemeModeState(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, []);

  const muiTheme = useMemo(() => createAppTheme(themeMode), [themeMode]);
  const value = useMemo(
    () => ({ themeMode, isDark: themeMode === 'dark', setThemeMode }),
    [themeMode, setThemeMode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};
